<template v-if="files?.length">
  <template v-for="file of files" :key="file.id">
    <div class="file-button">
      <a class="attachment-name p-d-flex p-ai-center"
         href="#"
         draggable="false"
         @click.prevent="downloadFile(file)"
         v-tooltip.top="fileTooltip(file)"
         style="padding: 2px 2px 2px 4px;">
        <template v-if="isImage(file)">
          <i class="pi pi-image order-file-icon"/>
        </template>
        <template v-else-if="isPdf(file)">
          <i class="pi pi-file-pdf order-file-icon"/>
        </template>
        <template v-else-if="isWord(file)">
          <i class="pi pi-file-word order-file-icon"/>
        </template>
        <template v-else-if="isExcel(file)">
          <i class="pi pi-file-excel order-file-icon"/>
        </template>
        <template v-else>
          <i class="pi pi-file order-file-icon"/>
        </template>

        <span class="word-break" style="margin: 0 2px 0 4px;">
          {{ file.source_name }}
        </span>
      </a>

      <Button @click.stop="deleteFileOnClick(file)"
              v-if="canDelete"
              v-tooltip.top="$t('Delete')"
              style="padding: 3px 5px; width: auto; height: auto;"
              :loading="loading || downloadingFileId === file.id"
              :disabled="loading || downloadingFileId === file.id"
              icon="ti-trash"
              class="p-button-sm p-button-text p-button-danger"/>
    </div>
  </template>
  <ConfirmDeleteModal :visible="confirmDeleteFileModal"
                      :modalDataIsLoading="dataIsSending"
                      :data="fileToDelete?.source_name"
                      @delete-item="deleteFile"
                      @close="closeConfirmDeleteFileModal"/>
</template>

<script>
import formatMixins from "@/mixins/formatMixins";
import httpClient from "@/services/http.services";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ formatMixins,showErrorsMixins ],
  emits: ['update-item', 'hide-file'],
  name: 'FilesList',
  watch: {
    '$store.state.firstLayerIsOpened'(value) {
      if (this.fromModal) return false
      if (!value) {
        if (this.confirmDeleteFileModal) {
          this.closeConfirmDeleteFileModal()
        }
      }
    },
    '$store.state.secondLayerIsOpened'(value) {
      if (!value) {
        if (this.confirmDeleteFileModal) {
          this.closeConfirmDeleteFileModal()
        }
      }
    },
  },
  props: {
    fromModal: {
      type: Boolean,
      default: false
    },
    itemId: {
      type: [Number, String],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false
    },
    files: {
      type: Array,
      default: () => [],
    },
    canDelete: {
      type: Boolean,
      default: true
    },
    entity: {
      type: String,
      default: 'order',
      validator: value => ['order', 'offer', 'ticket_message'].includes(value),
    },
  },
  data() {
    return {
      dataIsSending: false,
      downloadingFileId: null,
      fileToDelete: {},
      confirmDeleteFileModal: false,
    }
  },
  methods: {
    fileTooltip(file) {
      return `${this.$t('Size')}: ${this.formatSize(file.size)}, ${this.$t('Created')}: ${this.formatBy(file.created_at, file.createdBy)}`
    },
    getEntityConfig() {
      const map = {
        order: {
          download: 'order-file/download',
          delete: 'order-file/delete',
        },
        offer: {
          download: 'offer-file/download',
          delete: 'offer-file/delete',
        },
        ticket_message: {
          download: 'conversation-message-file/download',
          delete: 'conversation-message-file/delete',
        },
      }

      return map[this.entity] || null
    },
    async deleteFile() {
      const fileId = this.fileToDelete?.id
      // if (!fileId || !this.itemId || !this.canDelete) return
      if (!fileId || !this.canDelete) return

      const config = this.getEntityConfig()
      if (!config?.delete) return

      this.dataIsSending = true

      const link = config.delete

      try {
        const { status, data } = await httpClient.post(link, { id: fileId })
        if (status === 204 || (status === 200 && data?.success)) {
          this.closeConfirmDeleteFileModal()
          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data deleted'),
            life: settings.toastLife,
          })
          this.$emit('update-item', this.itemId, true)
          this.$emit('hide-file', fileId)
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
      }
    },
    deleteFileOnClick(file) {
      this.fileToDelete = file
      this.confirmDeleteFileModal = true
      if (!this.fromModal) {
        this.$store.commit('toggleFirstLayer', true)
      } else {
        this.$store.commit('toggleSecondLayer', true)
      }
    },
    closeConfirmDeleteFileModal() {
      this.confirmDeleteFileModal = false
      if (!this.fromModal) {
        this.$store.commit('toggleFirstLayer', false)
      } else {
        this.$store.commit('toggleSecondLayer', false)
      }

      this.fileToDelete = {}
    },
    async downloadFile(orderFileData) {
      const fileId = orderFileData?.id;
      // if (!fileId || !this.itemId || this.dataIsSending) return;
      if (!fileId || this.dataIsSending) return

      const config = this.getEntityConfig()
      if (!config?.download) return

      const link = config.download

      let objectUrl = null;

      this.downloadingFileId = fileId


      this.dataIsSending = true

      try {
        const { status, data, headers } = await httpClient(
            `${link}?id=${encodeURIComponent(fileId)}`,
            { responseType: 'arraybuffer' }
        );

        if (status !== 200 || !data) {
          throw new Error('Empty response');
        }

        // ----- MIME -----
        const mime =
            headers['content-type'] ||
            orderFileData.mime_type ||
            'application/octet-stream';

        // ----- Filename -----
        let filename =
            orderFileData.source_name ||
            orderFileData.name ||
            'file.bin';

        const cd = headers['content-disposition'];
        if (cd) {
          const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
          if (m) {
            try {
              filename = decodeURIComponent(m[1] || m[2]);
            } catch (_) {/**/}
          }
        }

        // ----- Blob -----
        const blob = new Blob([data], { type: mime });
        objectUrl = URL.createObjectURL(blob);

        // ----- Helper for forced download -----
        const downloadViaLink = () => {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          a.rel = 'noopener';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        };


        const OPENABLE_MIME = [
          // PDF
          'application/pdf',

          // Images
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/webp',
          'image/gif',
          'image/bmp',
          'image/svg+xml',
          'image/avif',
          'image/apng',

          // Text basic
          'text/plain',
          'text/html',
          'text/css',
          'text/xml',
          'text/csv',
          'text/markdown',
          'text/calendar',
          'text/javascript',

          // Application text-like
          'application/xml',
          'application/json',
          'application/xhtml+xml',
          'application/javascript',
          'application/x-javascript',
          'application/x-yaml'
        ];

        const ext = filename.split('.').pop()?.toLowerCase() || '';

        const OPENABLE_EXT = [
          // pdf
          'pdf',

          // images
          'png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg', 'avif', 'apng',

          // text-like
          'txt', 'html', 'css', 'xml', 'json',
          'csv', 'md', 'log', 'ics', 'yaml', 'yml'
        ];

        const canOpenAsBlob =
            OPENABLE_MIME.includes(mime.toLowerCase()) ||
            OPENABLE_EXT.includes(ext);

        if (canOpenAsBlob) {
          const win = window.open(objectUrl, '_blank');
          if (!win || win.closed || typeof win.closed === 'undefined') {
            downloadViaLink();
          }
        } else {
          downloadViaLink();
        }

      } catch (err) {
        this.showFileNotFoundError();
      } finally {
        this.downloadingFileId = null
        
        this.dataIsSending = false
        if (objectUrl) {
          setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        }
      }
    },
    getAttachmentName(att) {
      return (att?.source_name || att?.file_path || '').toLowerCase();
    },
    isImage(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      if (mime.startsWith('image/')) return true;

      const exts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
      return exts.some(ext => name.endsWith(ext));
    },
    isPdf(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      return mime === 'application/pdf' || name.endsWith('.pdf');
    },
    isWord(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      const wordMimes = [
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      ];

      if (wordMimes.includes(mime)) return true;

      const exts = ['.doc', '.docx'];
      return exts.some(ext => name.endsWith(ext));
    },
    isExcel(att) {
      const mime = att.mime_type || '';
      const name = this.getAttachmentName(att);

      const excelMimes = [
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      ];

      if (excelMimes.includes(mime)) return true;

      const exts = ['.xls', '.xlsx'];
      return exts.some(ext => name.endsWith(ext));
    },
  }
}
</script>
<style scoped>
.file-button {
  font-size: 0.9rem;
  margin: 1px 10px 1px 0;
  padding: 2px 3px 2px 0;
  display: inline-flex;
  align-items: center;
  width: auto;
  overflow: hidden;
}
</style>